import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ContentService from './ContentService';

/**
 * Uygulama genelinde dil ve çeviri işlemlerini yöneten servis sınıfı
 * Application katmanında yer alır
 */
export class LocalizationService {
  private static instance: LocalizationService;
  private isInitialized: boolean = false;
  private availableLanguages: string[] = ['en', 'tr'];
  private defaultLanguage: string = 'en';

  private constructor() {
    // Singleton yapı
  }

  /**
   * Singleton instance'ı getir
   */
  public static getInstance(): LocalizationService {
    if (!LocalizationService.instance) {
      LocalizationService.instance = new LocalizationService();
    }
    return LocalizationService.instance;
  }

  /**
   * Tarayıcı dilini algıla ve desteklenen diller arasında mı kontrol et
   * @returns Algılanan dil kodu veya varsayılan dil
   */
  private detectBrowserLanguage(): string {
    // Tarayıcı dilini al (ilk 2 karakteri - dil kodu)
    const browserLang = (
      navigator.language || 
      (navigator as any).userLanguage || 
      this.defaultLanguage
    ).split('-')[0].toLowerCase();
    
    console.log(`[LocalizationService] Tarayıcı dili algılandı: ${browserLang}`);
    
    // Desteklenen diller içinde mi kontrol et
    if (this.availableLanguages.includes(browserLang)) {
      return browserLang;
    }
    
    // Desteklenmiyorsa varsayılan dili döndür
    console.log(`[LocalizationService] Tarayıcı dili desteklenmiyor, varsayılan dil kullanılıyor: ${this.defaultLanguage}`);
    return this.defaultLanguage;
  }

  /**
   * i18n servisini başlat
   */
  public initialize(): void {
    if (this.isInitialized) return;

    try {
      // JSON dosyalarını require ile yükleme
      const resources = {
        en: {
          translation: require('../../infrastructure/i18n/en/translation.json'),
          terms: require('../../infrastructure/i18n/en/terms.json'),
          errors: require('../../infrastructure/i18n/en/errors.json')
        },
        tr: {
          translation: require('../../infrastructure/i18n/tr/translation.json'),
          terms: require('../../infrastructure/i18n/tr/terms.json'),
          errors: require('../../infrastructure/i18n/tr/errors.json')
        },
      };

      // Dil tespiti için öncelik sırası:
      // 1. localStorage'da kaydedilmiş dil
      // 2. Tarayıcı dili (destekleniyorsa)
      // 3. Varsayılan dil (en)
      
      const initialLanguage = localStorage.getItem('i18nextLng') || this.detectBrowserLanguage();
      
      i18n
        .use(LanguageDetector)
        .use(initReactI18next)
        .init({
          resources,
          lng: initialLanguage, // Başlangıç dilini belirle
          fallbackLng: this.defaultLanguage,
          interpolation: {
            escapeValue: false,
          },
          detection: {
            order: ['localStorage', 'navigator', 'htmlTag'],
            caches: ['localStorage'],
          },
        });

      this.isInitialized = true;
      console.log(`[LocalizationService] Başlatıldı, aktif dil: ${this.getCurrentLanguage()}`);
    } catch (error) {
      console.error('[LocalizationService] Başlatma hatası:', error);
    }
  }

  /**
   * Mevcut dili değiştir
   * @param language Yeni dil kodu
   */
  public changeLanguage(language: string): Promise<void> {
    console.log(`[LocalizationService] Dil değiştirme isteği: ${language}`);
    
    if (!this.isInitialized) {
      console.warn('[LocalizationService] Servis başlatılmadan dil değiştirme denendiği için otomatik başlatılıyor');
      this.initialize();
    }
    
    if (!this.availableLanguages.includes(language)) {
      console.warn(`[LocalizationService] Desteklenmeyen dil: ${language}, varsayılan dil kullanılıyor`);
      language = this.defaultLanguage;
    }

    // Eğer zaten seçili dil aynıysa işlem yapmaya gerek yok
    if (language === this.getCurrentLanguage()) {
      console.log(`[LocalizationService] Dil zaten ${language} olarak ayarlı, değişiklik yapılmadı`);
      return Promise.resolve();
    }

    // ContentService'deki cache'i temizle
    ContentService.clearCache();

    return i18n.changeLanguage(language)
      .then(() => {
        console.log(`[LocalizationService] Dil başarıyla değiştirildi: ${language}`);
        // Dil değişikliği için localStorage'a kaydet
        localStorage.setItem('i18nextLng', language);
      })
      .catch(error => {
        console.error(`[LocalizationService] Dil değiştirme hatası:`, error);
        throw error; // Hatayı yeniden fırlat ki çağıran taraf hatayı yönetebilsin
      });
  }

  /**
   * Mevcut dili getir
   */
  public getCurrentLanguage(): string {
    return i18n.language || this.defaultLanguage;
  }

  /**
   * Mevcut dil bilgisinin hangi dil olduğunu döndür
   */
  public getCurrentLanguageName(): string {
    const language = this.getCurrentLanguage();
    return language === 'tr' ? 'Türkçe' : 'English';
  }

  /**
   * Çeviri al
   * @param key Çeviri anahtarı
   * @param namespace Namespace
   * @param options Çeviri seçenekleri
   */
  public translate(key: string, namespace?: string, options: any = {}): string | object {
    const ns = namespace ? { ns: namespace } : {};
    return i18n.t(key, { ...ns, ...options });
  }

  /**
   * Belirli bir dil ve namespace için kaynak getir
   * @param language Dil kodu
   * @param namespace Namespace
   * @param key Key
   */
  public getResource(language: string, namespace: string, key: string): any {
    return i18n.getResource(language, namespace, key);
  }

  /**
   * Mevcut i18n nesnesini getir
   */
  public getI18nInstance(): typeof i18n {
    return i18n;
  }
}

// Singleton instance export et
export default LocalizationService.getInstance(); 